import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { Tag as PBTag, Tag_Type } from "gen/tag/models_pb"
import { ReactNode, useMemo } from "react"
import { UUID } from "shared-ts/classes/uuid"
import {
  Tag,
  extractTagColor,
} from "shared-web/components/base/informational/Tag"

import { useSearchTags } from "features/api/search"

import { UsePopoverMultiSelectProps, UsePopoverMultiSelectRes } from "./types"
import usePopoverMultiSelect from "./usePopoverMultiSelect"

interface UseTagsPopoverMultiSelectProps
  extends Omit<
    UsePopoverMultiSelectProps<PBTag>,
    | "items"
    | "keyExtractor"
    | "labelExtractor"
    | "contentExtractor"
    | "onChangeQuery"
  > {
  tagType: Tag_Type
}

export interface UseTagsPopoverMultiSelectRes
  extends UsePopoverMultiSelectRes<PBTag> {
  selectedIds: PBUUID[]
}

function useTagsPopoverMultiSelect({
  tagType,
  ...rest
}: UseTagsPopoverMultiSelectProps): UseTagsPopoverMultiSelectRes {
  const { results, setQuery } = useSearchTags(
    { types: [tagType] },
    { ignoreSearchQuery: true }
  )

  const tags = useMemo(() => {
    const res: PBTag[] = []
    for (const { tag } of results) {
      if (!tag) continue
      res.push(tag)
    }
    return res
  }, [results])

  const res = usePopoverMultiSelect({
    items: tags,
    keyExtractor,
    labelExtractor,
    contentExtractor,
    onChangeQuery: setQuery,
    ...rest,
  })

  const selectedIds = useMemo(() => {
    if (!res.selected) return []
    return res.selected
      .map((tag) => tag.id)
      .filter((id): id is PBUUID => id !== undefined)
  }, [res.selected])

  return {
    ...res,
    selectedIds,
  }
}

function keyExtractor(tag: PBTag): string {
  return UUID.idString(tag.id)
}

function labelExtractor(tag: PBTag): string {
  return tag.name
}

function contentExtractor(tag: PBTag): ReactNode {
  return <Tag name={tag.name} color={extractTagColor(tag.color)} />
}

export default useTagsPopoverMultiSelect
